import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';

// ── Deciding an overtime claim from the Actions card ─────────────────────────
//
// The approval used to live only on the Approval Requests screen, which meant
// the action in a manager's queue could do nothing but point at it. The
// decision belongs where the person is looking, so it is made here.
//
// What is NOT here is any of the approval's rules. Approve and Reject go to the
// ordinary PUT /overtime-requests/:id, the same call the Approval Requests
// modal makes, so the TOIL allowance credit, the grade cap and the email to the
// claimant all stay on the server in one place. A refusal (a TOIL claim that
// would take somebody past their yearly cap, say) comes back as an error and is
// shown in this panel, next to the button that was pressed, with the claim
// still open to act on.
//
// What the approver is not asked is what to pay. The policy fixes that from the
// date the work fell on, and the figure is finance's to see on the Overtime
// page. A manager decides whether the work was overtime, not what an hour of it
// is worth, so the decision here is Approve or Reject and nothing else.
//
// Either way it carries a note forward to the claimant. A rejection has to
// explain itself, so its reason is required; an approval usually needs nothing
// said, so its note is optional. The card offers no Mark done, Resolve all or
// Delete for a claim, because each of those closed the queue item and left the
// claim pending, so the note has nowhere else to be written.

// The pending claims, loaded once and shared by every card on the page rather
// than fetched per card. Short-lived: a decision invalidates it, and anything
// older than the window is refetched, so a claim decided in another tab does
// not linger here as still-pending.
const FRESH_MS = 15000;
let pendingCache = null;
let pendingFetchedAt = 0;

function loadPendingClaims({ force } = {}) {
  if (force || !pendingCache || Date.now() - pendingFetchedAt > FRESH_MS) {
    pendingFetchedAt = Date.now();
    pendingCache = api.get('overtime-requests?status=pending')
      .then(d => (Array.isArray(d) ? d : []))
      .catch(() => []);
  }
  return pendingCache;
}

/**
 * The decision panel on an action whose entity is an overtime claim.
 *
 * @param requestId  the overtime_requests id the action points at
 * @param canDecide  whether this person is the one being asked (an assignee)
 * @param onDecided  called after a decision lands, so the queue reloads
 */
export default function OvertimeActionDecision({ requestId, canDecide, onDecided }) {
  const [claim, setClaim]   = useState(undefined);   // undefined = loading, null = not pending
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote]     = useState('');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');

  const refresh = useCallback((force) => {
    loadPendingClaims({ force }).then(rows => {
      setClaim(rows.find(r => r.id === requestId) || null);
    });
  }, [requestId]);

  useEffect(() => { refresh(false); }, [refresh]);

  const decide = async (body) => {
    setBusy(true); setError('');
    try {
      await api.put(`overtime-requests/${requestId}`, body);
      loadPendingClaims({ force: true });
      onDecided && onDecided();
    } catch (e) {
      setError(e?.message || 'That could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (claim === undefined) {
    return <div className="text-[9px] text-gray-400 mt-1.5">Loading the claim…</div>;
  }

  // Decided elsewhere between the queue loading and this card rendering. Said
  // plainly rather than left as controls that would fail when pressed.
  if (claim === null) {
    return (
      <div className="mt-1.5 text-[9px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
        This claim is no longer pending. It has been decided somewhere else.
        <button onClick={() => refresh(true)} className="ml-1 text-navy underline">Check again</button>
      </div>
    );
  }

  return (
    <div className="mt-1.5 bg-gray-50 border border-gray-200 rounded px-2 py-1.5" onClick={e => e.stopPropagation()}>
      {claim.reason && (
        <div className="text-[9px] text-gray-600 mb-1.5 whitespace-pre-wrap break-words">{claim.reason}</div>
      )}

      {canDecide && !rejecting && (
        <div className="space-y-1.5">
          <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} disabled={busy}
            placeholder="Anything to pass on with the approval? The claimant is sent this. (optional)"
            className="w-full text-[9px] border border-gray-300 rounded px-1.5 py-1 bg-white text-navy" />
          <div className="flex items-center gap-1.5">
            <button onClick={() => decide({ status: 'approved', decision_note: note.trim() || null })} disabled={busy}
              title="Approve this claim"
              className="px-2.5 py-1 text-[9px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-30">
              {busy ? 'Saving…' : '✓ Approve'}
            </button>
            <button onClick={() => { setError(''); setRejecting(true); }} disabled={busy}
              className="px-2.5 py-1 text-[9px] font-semibold border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-30">
              Reject
            </button>
          </div>
        </div>
      )}

      {canDecide && rejecting && (
        <div className="mt-1.5 space-y-1.5">
          <textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} disabled={busy}
            placeholder="Why is it being rejected? The claimant is told this."
            className="w-full text-[9px] border border-gray-300 rounded px-1.5 py-1 bg-white text-navy" />
          <div className="flex items-center gap-1.5">
            <button onClick={() => decide({ status: 'rejected', rejection_reason: reason.trim() })}
              disabled={busy || !reason.trim()}
              className="px-2.5 py-1 text-[9px] font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-30">
              {busy ? 'Saving…' : 'Reject the claim'}
            </button>
            <button onClick={() => { setRejecting(false); setReason(''); }} disabled={busy}
              className="px-2.5 py-1 text-[9px] font-semibold border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-30">
              Keep it open
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-1.5 text-[9px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 whitespace-pre-wrap">{error}</div>
      )}
    </div>
  );
}
